import type { Account, Flag, Pickup } from "./api";
import { ROUGH_PIN } from "./location";

export const FLAG_TEXT: Record<Flag, string> = {
  near_full: "Near full",
  small_container: "Small container",
  rate_high: "Rate high",
  rate_low: "Rate low",
  high_scrap: "High scrap",
  seasonal: "Seasonal",
};

/** The hover help of each flag (rule U-28). The limits are on Admin > Forecast limits (design 8.4). */
export const FLAG_HELP: Record<Flag, string> = {
  near_full: "The fill estimate (the level dot on the maps) is more than the near full limit of the container size (default 75%). With no estimate yet, the load from the stated rate. Check the next visit.",
  small_container: "The containers hold fewer weeks of oil than the small container limit (default 5 weeks). A larger container is recommended.",
  rate_high: "The measured rate is more than the stated rate × the rate band (default 1.25). The stated rate is possibly too low.",
  rate_low: "The measured rate is less than the stated rate ÷ the rate band (default 1.25). The stated rate is possibly too high, or the customer gives oil to a different collector.",
  high_scrap: "The scrap share of the last 13 weeks is more than the high scrap limit (default 25%). The oil is possibly poor, or it has water.",
  seasonal: "At least 75% of the pickups are in one window of 5 months, and the service plan has no season. Set the season on the service plan, so that the forecast stops in the off season.",
};

export const NO_CONTAINER_HELP = "The account has no container. Add the container on the account page.";

/** The help of a flag; with no container, the help of "No container". */
export const flagHelp = (account: Pick<Account, "capacity_gal">, flag: Flag) =>
  flag === "small_container" && account.capacity_gal <= 0 ? NO_CONTAINER_HELP : FLAG_HELP[flag];

export const OFF_SEASON_HELP = "The account is in its off season. The rotation plans no visit until the season opens again.";

export const STATUS_TEXT: Record<Account["status"], string> = {
  NEW: "New",
  REG: "Regular",
  CALL: "On call",
  HOLD: "On hold",
  OFF: "Ended",
};

/** The hover help of each service status (design 8.1). */
export const STATUS_HELP: Record<Account["status"], string> = {
  NEW: "Signed. The driver has not delivered the container, or has not done the first pickup. The account goes in the rotation after the setup stop.",
  REG: "Regular service on the rotation.",
  CALL: "Service only when the customer calls. A call makes a stop.",
  HOLD: "Service is paused, for example for a season or a dispute. The account is not in the rotation.",
  OFF: "Service ended. The history stays.",
};

export interface RowGroup {
  title: string;
  accounts: Account[];
}

export interface Section {
  key: string;
  title: string;
  label: string;
  groups: RowGroup[];
}

const IN_ROTATION = new Set<Account["status"]>(["NEW", "REG"]);

/** Design 8.1: On call, On hold and Ended accounts are not in the rotation, so they get no flags. */
export const inRotation = (account: Account) => IN_ROTATION.has(account.status);

/** Rule A-9: On call and On hold accounts also show Seasonal, so that sales can set the season. */
export const flagsOf = (account: Account): Flag[] => {
  const flags = account.projection?.flags ?? [];
  if (inRotation(account)) return flags;
  return account.status === "OFF" ? [] : flags.filter((flag) => flag === "seasonal");
};

/** The flag words; with no container, "Small container" is "No container". */
export const flagText = (account: Pick<Account, "capacity_gal">, flag: Flag) =>
  flag === "small_container" && account.capacity_gal <= 0 ? "No container" : FLAG_TEXT[flag];

export const nearFullCount = (accounts: Account[]) =>
  accounts.filter((account) => flagsOf(account).includes("near_full")).length;

export const accountCount = (count: number) => `${count} ${count === 1 ? "account" : "accounts"}`;

export function weeksOf(account: Account): number[] {
  if (!account.plan) return [];
  const weeks = account.plan.week_group.split("-").map(Number);
  return weeks.includes(account.plan.rotation_week) ? weeks : [account.plan.rotation_week, ...weeks];
}

const byNumber = (a: Account, b: Account) => a.number.localeCompare(b.number, undefined, { numeric: true });

function grouped(accounts: Account[], keyOf: (account: Account) => string, order: string[] = []) {
  const groups = new Map<string, Account[]>();
  for (const account of accounts) groups.set(keyOf(account), [...(groups.get(keyOf(account)) ?? []), account]);
  const rank = (key: string) => (order.includes(key) ? order.indexOf(key) : order.length);
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([key, list]) => ({ key, accounts: [...list].sort(byNumber) }));
}

/** Rotation weeks hold only accounts in the rotation, once per week of the week group (design 8.1). */
export function groupAccounts(accounts: Account[]): Section[] {
  const sections: Section[] = [];
  const rotation = accounts.filter((account) => account.plan && inRotation(account));
  for (const week of [1, 2, 3, 4]) {
    const due = rotation.filter((account) => weeksOf(account).includes(week));
    if (!due.length) continue;
    sections.push({
      key: `week-${week}`,
      title: `Rotation week ${week}`,
      label: `Rotation week ${week} accounts`,
      groups: grouped(due, (account) => account.plan?.area ?? "").map(({ key, accounts: list }) => ({
        title: `Area ${key}`,
        accounts: list,
      })),
    });
  }
  const out = accounts.filter((account) => account.plan && !inRotation(account));
  if (out.length)
    sections.push({
      key: "out",
      title: "Not in the rotation",
      label: "Accounts not in the rotation",
      groups: grouped(out, (account) => account.status, ["CALL", "HOLD", "OFF"]).map(({ key, accounts: list }) => ({
        title: STATUS_TEXT[key as Account["status"]],
        accounts: list,
      })),
    });
  const unplanned = accounts.filter((account) => !account.plan);
  if (unplanned.length)
    sections.push({
      key: "none",
      title: "No service plan",
      label: "Accounts with no service plan",
      groups: [{ title: "", accounts: [...unplanned].sort(byNumber) }],
    });
  return sections;
}

export function weekGroupText(account: Account): string {
  const weeks = weeksOf(account);
  if (weeks.length <= 1) return "";
  if (weeks.length === 4) return "Every week";
  if (weeks.length === 2 && Math.abs(weeks[1] - weeks[0]) === 2) return `Every 2 weeks (${account.plan?.week_group})`;
  return `Weeks ${account.plan?.week_group}`;
}

/** The fill of the Load and Full cells: the fill estimate (the level dot), else the projection from the stated rate. */
export function fillOf(account: Pick<Account, "status" | "projection">): { share: number; estimated: boolean } | null {
  const projection = account.projection;
  if (!projection || account.status === "OFF") return null;
  const median = projection.estimate?.today.p50;
  if (median != null) return { share: median, estimated: true };
  return projection.load_share == null ? null : { share: projection.load_share, estimated: false };
}

export const STATED_RATE_HELP = "From the stated rate; no estimate yet.";

/** The gallons of the Load cell: the fill estimate × the container size, else the projected load. */
export function loadGal(account: Pick<Account, "status" | "projection" | "capacity_gal">): number | null {
  const fill = fillOf(account);
  if (fill?.estimated) return fill.share * account.capacity_gal;
  return account.status === "OFF" ? null : (account.projection?.load_gal ?? null);
}

export function loadText(account: Account, withHold = true): string {
  if (!account.projection) return account.last_pickup || account.status === "OFF" ? "–" : "No pickups yet";
  if (account.status === "OFF" || !(account.capacity_gal > 0)) return "–";
  const load = loadGal(account);
  if (load == null) return "No pickups yet";
  const fill = fillOf(account);
  const percent = fill == null ? "" : ` (${Math.round(Math.min(fill.share, 1) * 100)}%)`;
  const hold = withHold && account.status === "HOLD" ? " · on hold" : "";
  const shown = account.capacity_gal > 0 ? Math.min(load, account.capacity_gal) : load;
  const about = fill?.estimated ? "About " : "";
  return `${about}${Math.round(shown)} of ${Math.round(account.capacity_gal)} gal${percent}${hold}`;
}

/** The Load cell of the Accounts list on two lines, "106 gal" and "of 150 gal", so the default columns fit at 1280 px. */
export function loadParts(account: Account): { text: string; of: string | null } {
  const text = loadText(account, false);
  const load = loadGal(account);
  if (load == null || !/\d/.test(text)) return { text, of: null };
  const shown = account.capacity_gal > 0 ? Math.min(load, account.capacity_gal) : load;
  return { text: `${Math.round(shown)} gal`, of: `of ${Math.round(account.capacity_gal)} gal` };
}

export function weeksLeftText(account: Account): string {
  const left = account.projection?.weeks_left;
  if (account.projection?.off_season) return "– (off season)";
  if (left == null || !inRotation(account)) return "–";
  if (left <= 0) return "Due now";
  return left === 1 ? "1 week" : `${left} weeks`;
}

/** A pickup has a date and no time, so it is shown as that calendar date. */
export function dayText(iso: string, year = false): string {
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", timeZone: "UTC" };
  if (year) options.year = "numeric";
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", options);
}

export function lastPickupText(account: Account): string {
  return account.last_pickup ? `Last pickup ${dayText(account.last_pickup)}` : "";
}

const tenths = (value: number) => String(Math.round(value * 10) / 10);

/** "1,200.5": tenths, with commas. */
export const gallons = (value: number) => (Math.round(value * 10) / 10).toLocaleString("en-US");

export const galText = (value: number) => `${gallons(value)} gal`;

export const netGal = (pickup: Pickup) => Math.max(0, pickup.raw_gal - pickup.scrap_gal);

export interface PickupRow {
  pickup: Pickup;
  replacedBy?: Pickup;
  replaces?: Pickup;
}

/** A corrected pickup stays in the history. The projection uses only the correction. */
export function pickupRows(pickups: Pickup[]): PickupRow[] {
  const byId = new Map(pickups.map((pickup) => [pickup.id, pickup]));
  const replacement = new Map(
    pickups.flatMap((pickup): [string, Pickup][] => (pickup.corrects_id ? [[pickup.corrects_id, pickup]] : [])),
  );
  return pickups.map((pickup) => ({
    pickup,
    replacedBy: replacement.get(pickup.id),
    replaces: pickup.corrects_id ? byId.get(pickup.corrects_id) : undefined,
  }));
}

/** Design 8.3: the rate used is the mean of the measured rate and the stated rate. */
export function rateText(account: Account): string {
  if (!account.projection || !account.plan) return "–";
  const { rate_used, measured_rate, rated, restart } = account.projection;
  if (rated === false && restart) {
    return `${tenths(rate_used)} gal each week: stated, no pickups since the forecast start on ${dayText(restart)}`;
  }
  return `${tenths(rate_used)} gal each week: the mean of ${tenths(measured_rate)} from the pickups and ${tenths(account.plan.stated_rate)} stated`;
}

export function weeksOutText(account: Account): string {
  const out = account.projection?.weeks_out;
  if (out == null) return "No pickups yet";
  const since = account.projection?.from_restart ? "the forecast start" : "the last pickup";
  if (out === 0) return account.projection?.from_restart ? "Forecast start this week" : "Pickup this week";
  return out === 1 ? `1 week since ${since}` : `${out} weeks since ${since}`;
}

/** The newest day the weeks out count from: a pickup, or the forecast start (A-5). */
export function newestText(account: Account): string {
  const { from_restart, restart } = account.projection ?? {};
  if (from_restart && restart) return `Forecast start ${dayText(restart, true)} · ${weeksOutText(account)}`;
  return account.last_pickup ? `${dayText(account.last_pickup, true)} · ${weeksOutText(account)}` : weeksOutText(account);
}

export function capWeeksText(account: Account): string {
  const weeks = account.projection?.cap_weeks;
  return weeks == null || account.capacity_gal <= 0 ? "–" : `${tenths(weeks)} weeks at the stated rate`;
}

export function scrapText(account: Account): string {
  const scrap = account.projection?.scrap;
  if (scrap == null) return "–";
  const percent = `${Math.round(scrap * 100)}%`;
  const restart = account.projection?.restart;
  if (restart && account.projection?.rated === false) {
    return `${percent}, the default: no pickups since the forecast start on ${dayText(restart)}`;
  }
  if (account.projection?.averages?.[0] === 0) {
    return `${percent}, the default: no pickups in the last 13 weeks`;
  }
  return `${percent} of the raw gallons in the last 13 weeks`;
}

export function windowText(account: Account): string {
  const [start, end] = account.plan?.window ?? [null, null];
  const minutes = account.plan?.service_minutes;
  const time = start && end ? `${start}–${end}` : "Any time";
  return minutes ? `${time} · ${minutes} min at the stop` : time;
}

/** Pins to check: each account once, the rough geocodes first. */
export const pinRows = (accounts: readonly Account[]): Account[] =>
  [...new Map(accounts.map((account) => [account.id, account])).values()].sort(
    (a, b) => Number(Boolean(b.door?.check)) - Number(Boolean(a.door?.check)) || byNumber(a, b),
  );

export const pinStateText = (account: Account) => (account.door?.check ? ROUGH_PIN : "Geocode");
